/**
 * Sprint Metrics Template — Excel Template Generation
 *
 * Generates a pre-formatted .xlsx template file that users can fill in
 * and import back with 100% detection confidence (exact header matches).
 */

import * as XLSX from 'xlsx';
import { ALL_METRIC_FIELDS, METRIC_LABELS, SprintInfo } from './sprintImport';

/**
 * Generate an Excel template file for sprint metrics import.
 *
 * @param sprints - Sprints for the selected year (in order)
 * @param teamName - Team name (used as sheet name)
 * @returns Blob containing the .xlsx file
 */
export function generateTemplate(sprints: SprintInfo[], teamName: string): Blob {
  // Build header row: Sprint Name + all 9 metric columns
  const headers = ['Sprint', ...ALL_METRIC_FIELDS.map((f) => METRIC_LABELS[f])];

  // Build data rows: sprint names pre-filled, metric cells empty
  const dataRows = sprints.map((sprint) => [
    sprint.name,
    ...ALL_METRIC_FIELDS.map(() => ''),
  ]);

  const sheetData = [headers, ...dataRows];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths (Sprint name wider, metric columns uniform)
  worksheet['!cols'] = [
    { wch: 16 }, // Sprint name
    ...ALL_METRIC_FIELDS.map(() => ({ wch: 14 })),
  ];

  // Sanitize sheet name: Excel limits to 31 chars, no special chars [ ] : * ? / \
  const sanitizedName = teamName
    .replace(/[[\]:*?/\\]/g, '_')
    .slice(0, 31);

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizedName || 'Metrics');

  // Write to binary buffer and create Blob
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
